import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { 
  Megaphone, 
  Plus, 
  Trash2, 
  AlertTriangle,
  Clock,
  User,
  Edit,
  Link,
  ExternalLink,
  History
} from "lucide-react";
import { format } from "date-fns";
import { sl } from "date-fns/locale";

const AnnouncementsPage = () => {
  const { user, token, hasRole } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  const [archivedItems, setArchivedItems] = useState([]);
  const [formData, setFormData] = useState({
    naslov: "",
    vsebina: "",
    pomembno: false,
    povezava: "",
    datum_prikaza_od: "",
    datum_prikaza_do: "",
  });

  useEffect(() => {
    fetchAnnouncements();
    // Označi kategorijo kot prebrano ob odprtju strani
    const headers = { Authorization: `Bearer ${token}` };
    axios.post(`${API}/me/notification-prefs/mark-seen`, { category: "obvestila" }, { headers }).catch(() => { /* silent */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAnnouncements = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/announcements`, { headers });
      setAnnouncements(response.data);
    } catch (error) {
      toast.error("Napaka pri nalaganju obvestil");
    } finally {
      setLoading(false);
    }
  };

  const fetchArchive = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/announcements/archive`, { headers });
      setArchivedItems(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Nimate dostopa do arhiva");
      } else {
        toast.error("Napaka pri nalaganju arhiva");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...formData,
        datum_prikaza_od: formData.datum_prikaza_od || null,
        datum_prikaza_do: formData.datum_prikaza_do || null,
      };
      
      if (editingAnnouncement) {
        await axios.put(`${API}/announcements/${editingAnnouncement.id}`, payload, { headers });
        toast.success("Obvestilo posodobljeno");
      } else {
        await axios.post(`${API}/announcements`, payload, { headers });
        toast.success("Obvestilo objavljeno");
      }
      
      setDialogOpen(false);
      setEditingAnnouncement(null);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      naslov: announcement.naslov || "",
      vsebina: announcement.vsebina || "",
      pomembno: announcement.pomembno || false,
      povezava: announcement.povezava || "",
      datum_prikaza_od: announcement.datum_prikaza_od || "",
      datum_prikaza_do: announcement.datum_prikaza_do || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Ali ste prepričani, da želite izbrisati obvestilo?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/announcements/${id}`, { headers });
      toast.success("Obvestilo izbrisano");
      fetchAnnouncements();
    } catch (error) {
      toast.error("Napaka pri brisanju");
    }
  };

  const resetForm = () => {
    setFormData({
      naslov: "",
      vsebina: "",
      pomembno: false,
      povezava: "",
      datum_prikaza_od: "",
      datum_prikaza_do: "",
    });
    setEditingAnnouncement(null);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, "d. MMMM yyyy 'ob' HH:mm", { locale: sl });
  };

  const canManage = hasRole(["predsednik", "tajnik"]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="announcements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-3">
            <Megaphone className="h-8 w-8" />
            Obvestila
          </h1>
          <p className="text-muted-foreground mt-1">
            Obvestila in novice lovske družine
          </p>
        </div>
        
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={async () => {
              if (!showArchive) { await fetchArchive(); }
              setShowArchive((v) => !v);
            }} data-testid="toggle-archive-btn">
              <History className="h-4 w-4 mr-2" />
              {showArchive ? "Skrij arhiv" : "Arhiv"}
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-announcement-btn">
                <Plus className="h-4 w-4 mr-2" />
                Novo obvestilo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingAnnouncement ? "Uredi obvestilo" : "Novo obvestilo"}</DialogTitle>
                <DialogDescription>
                  {editingAnnouncement ? "Uredite podatke obvestila" : "Objavite novo obvestilo za vse člane"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="naslov">Naslov</Label>
                  <Input
                    id="naslov"
                    value={formData.naslov}
                    onChange={(e) => setFormData({ ...formData, naslov: e.target.value })}
                    placeholder="Naslov obvestila"
                    required
                    data-testid="announcement-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vsebina">Vsebina</Label>
                  <Textarea
                    id="vsebina"
                    value={formData.vsebina}
                    onChange={(e) => setFormData({ ...formData, vsebina: e.target.value })}
                    placeholder="Vsebina obvestila..."
                    rows={5}
                    required
                    data-testid="announcement-content-input"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="pomembno"
                    checked={formData.pomembno}
                    onCheckedChange={(checked) => setFormData({ ...formData, pomembno: checked })}
                    data-testid="announcement-important-checkbox"
                  />
                  <Label htmlFor="pomembno" className="text-sm font-normal cursor-pointer">
                    Označi kot pomembno obvestilo
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="povezava" className="flex items-center gap-2">
                    <Link className="h-4 w-4" />
                    Povezava (neobvezno)
                  </Label>
                  <Input
                    id="povezava"
                    value={formData.povezava}
                    onChange={(e) => setFormData({ ...formData, povezava: e.target.value })}
                    placeholder="https://..."
                    data-testid="announcement-link-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="datum_prikaza_od" className="text-xs">Prikaži od (neobvezno)</Label>
                    <Input
                      id="datum_prikaza_od"
                      type="date"
                      value={formData.datum_prikaza_od}
                      onChange={(e) => setFormData({ ...formData, datum_prikaza_od: e.target.value })}
                      data-testid="announcement-visibility-from"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="datum_prikaza_do" className="text-xs">Prikaži do (neobvezno, potem arhiv)</Label>
                    <Input
                      id="datum_prikaza_do"
                      type="date"
                      value={formData.datum_prikaza_do}
                      onChange={(e) => setFormData({ ...formData, datum_prikaza_do: e.target.value })}
                      data-testid="announcement-visibility-to"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Če pustite prazno, obvestilo se prikaže takoj in trajno. Po datumu &bdquo;Prikaži do&rdquo; se skrije, ostane pa v arhivu.
                </p>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Prekliči
                  </Button>
                  <Button type="submit" data-testid="save-announcement-btn">
                    Objavi
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Announcements List */}
      <div className="space-y-4">
        {showArchive && (
          <Card className="border-2 border-amber-300 bg-amber-50/40" data-testid="archive-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4" />
                Arhiv obvestil ({archivedItems.length})
              </CardTitle>
              <p className="text-xs text-muted-foreground">Obvestila, ki jim je potekel datum &bdquo;Prikaži do&rdquo;.</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {archivedItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">Arhiv je prazen.</p>
              ) : (
                <ul className="divide-y divide-amber-200">
                  {archivedItems.map((ann) => (
                    <li key={ann.id} className="py-2 flex items-start justify-between gap-2" data-testid={`archived-${ann.id}`}>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{ann.naslov}</p>
                        <p className="text-xs text-muted-foreground">Skrito od {ann.datum_prikaza_do}</p>
                      </div>
                      {canManage && (
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(ann)}>
                          Uredi
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
        {announcements.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Megaphone className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">Ni obvestil</p>
                {canManage && (
                  <Button className="mt-4" onClick={() => setDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Dodaj prvo obvestilo
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          announcements.map((ann) => (
            <Card 
              key={ann.id} 
              className={`${ann.pomembno ? 'border-l-4 border-l-orange-500 bg-orange-50/50' : ''}`}
              data-testid={`announcement-${ann.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {ann.pomembno && (
                        <Badge className="bg-orange-100 text-orange-800">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Pomembno
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-xl font-semibold">{ann.naslov}</h3>
                    <p className="text-muted-foreground mt-2 whitespace-pre-wrap">
                      {ann.vsebina}
                    </p>
                    {ann.povezava && (
                      <a 
                        href={ann.povezava} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-3 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        data-testid={`announcement-link-${ann.id}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                        Odpri povezavo
                      </a>
                    )}
                    <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {ann.avtor_ime}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {formatDate(ann.ustvarjeno)}
                      </span>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-primary hover:text-primary hover:bg-primary/10"
                        onClick={() => handleEdit(ann)}
                        data-testid={`edit-announcement-${ann.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleDelete(ann.id)}
                        data-testid={`delete-announcement-${ann.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AnnouncementsPage;
